import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';

import useColors from 'hooks/use-colors';

import { DutchieLogo } from 'assets/marketing/rebrand';
import { VerticalDivider, Link, ContentWrapper } from 'components/core';
import { CartButton } from 'components/cart';
import { CartButtonVariant } from 'components/cart/button';
import { AddressTextButton, LocationSettingsPopover } from 'components/location-settings-popover';

import { MyAccountButton } from '../my-account-button';
import { ShopDropdown } from '../shop-dropdown';
import { useGlobalHeader } from './hooks/use-global-header';

export const GlobalHeader = observer(
  (): JSX.Element => {
    const {
      cartButtonRef,
      globalHeaderRef,
      addressDropdownContainerRef,
      shopDropdownContainerRef,
      itemCount,
      toggleLocationSettingsDropdown,
      toggleCartView,
      href,
      route,
      address,
      orderType,
      onSaveLocationSettings,
    } = useGlobalHeader();
    const colors = useColors();

    return (
      <Wrapper id='global-header' ref={globalHeaderRef}>
        <ContentWrapper bg={colors.opal04} flexGrow={0} maxWidth={1400}>
          <Container>
            <Link href={href} route={route} pr={30}>
              <a data-testid='dutchie-icon' href={href}>
                <DutchieLogo height='33px' color='white' />
              </a>
            </Link>

            <ContentGroup>
              <HeaderDivider mx='30px' />
              <DropdownContainer ref={shopDropdownContainerRef}>
                <ShopDropdown anchorEl={shopDropdownContainerRef.current} />
              </DropdownContainer>
              <SearchPlaceholder />
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <DropdownContainer ref={addressDropdownContainerRef} data-testid='dropdown-web'>
                <AddressTextButton onClick={toggleLocationSettingsDropdown} address={address} orderType={orderType} />
                <LocationSettingsPopover
                  anchorEl={addressDropdownContainerRef.current as HTMLElement}
                  onSave={onSaveLocationSettings}
                />
              </DropdownContainer>
            </ContentGroup>

            <ContentGroup>
              <HeaderDivider mx='23px' />
              <DropdownContainer>
                <MyAccountButton />
              </DropdownContainer>

              <DropdownContainer>
                <CartButton
                  ref={cartButtonRef}
                  onClick={toggleCartView}
                  variant={CartButtonVariant.darkBlue}
                  itemCount={itemCount}
                  showCountColor
                />
              </DropdownContainer>
            </ContentGroup>
          </Container>
        </ContentWrapper>
      </Wrapper>
    );
  }
);

const Wrapper = styled.div`
  box-shadow: 0px 3px 2px rgba(0, 0, 0, 0.0579928);
  width: 100%;

  /* location-settings-modal mui backdrop z-index + 100. So the backdrop doesn't cover the global nav */
  z-index: 1200;

  .content-wrapper {
    max-width: 100vw;
  }
`;

const Container = styled.header`
  position: relative;
  z-index: 100;
  display: flex;
  height: 70px;
  align-items: center;
  padding-left: 34px;
  padding-right: 24px;
  * {
    .focus-visible {
      box-shadow: ${({ theme }) => `0 0 0pt 2pt ${String(theme.colors.whiteOverlay)}`};
    }
  }
  div:nth-child(2) {
    flex: 1;
  }
`;

const ContentGroup = styled.div`
  display: flex;
  align-items: center;
`;

const HeaderDivider = styled(VerticalDivider)`
  border-color: ${({ theme }) => theme.colors.opal05};
  height: 34px;
  margin-bottom: 1px;
`;

const SearchPlaceholder = styled.div`
  flex: 1;
`;

const DropdownContainer = styled.div`
  align-items: center;
  position: relative;
`;
